"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { Thumb } from './EmblaCarouselThumbsButton';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Image from "next/image";

interface ProductImageSliderProps {
    slides: string[];
    options?: any;
    imageFit?: 'contain' | 'cover';
}

const ProductImageSlider: React.FC<ProductImageSliderProps> = ({ slides, options, imageFit = 'cover' }) => {
    const slideItems = Array.isArray(slides) ? slides.filter(Boolean) : [];
    const hasMultipleSlides = slideItems.length > 1;

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options);
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true
    });

    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalStartIndex, setModalStartIndex] = useState(0);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const [modalSelectedIndex, setModalSelectedIndex] = useState(0);
    const [emblaModalRef, emblaModalApi] = useEmblaCarousel(options);
    const [emblaModalThumbsRef, emblaModalThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true
    });
    const [modalPrevBtnEnabled, setModalPrevBtnEnabled] = useState(false);
    const [modalNextBtnEnabled, setModalNextBtnEnabled] = useState(false);
    const [modalScrollSnaps, setModalScrollSnaps] = useState<number[]>([]);

    // --- Коллбэк для клика по миниатюрам ---
    const onThumbClick = useCallback((index: number) => {
        if (!emblaMainApi || !emblaThumbsApi) return;
        emblaMainApi.scrollTo(index);
    }, [emblaMainApi, emblaThumbsApi]);

    // --- Синхронизируем состояние при переключении слайдов ---
    const onSelect = useCallback(() => {
        if (!emblaMainApi) return;
        const newIndex = emblaMainApi.selectedScrollSnap();
        setSelectedIndex(newIndex);
        // Обновляем кнопки "prev"/"next"
        setPrevBtnEnabled(emblaMainApi.canScrollPrev());
        setNextBtnEnabled(emblaMainApi.canScrollNext());

        // Синхронизируем слайд в миниатюрах
        if (emblaThumbsApi) emblaThumbsApi.scrollTo(newIndex);
    }, [emblaMainApi, emblaThumbsApi]);

    // --- Обработчики для стрелок ---
    const scrollPrev = useCallback(() => {
        if (!emblaMainApi) return;
        emblaMainApi.scrollPrev();
    }, [emblaMainApi]);

    const scrollNext = useCallback(() => {
        if (!emblaMainApi) return;
        emblaMainApi.scrollNext();
    }, [emblaMainApi]);

    // --- Клик по дотсу ---
    const onDotClick = useCallback((index: number) => {
        if (!emblaMainApi) return;
        emblaMainApi.scrollTo(index);
    }, [emblaMainApi]);

    // --- Инициализация: заполняем snapList и подписываемся на события ---
    useEffect(() => {
        if (!emblaMainApi) return;
        setScrollSnaps(emblaMainApi.scrollSnapList());
        onSelect();
        emblaMainApi.on('select', onSelect);
        emblaMainApi.on('reInit', onSelect);
    }, [emblaMainApi, onSelect]);

    // --- Клик по миниатюрам в модалке ---
    const onModalThumbClick = useCallback((index: number) => {
        if (!emblaModalApi || !emblaModalThumbsApi) return;
        emblaModalApi.scrollTo(index);
    }, [emblaModalApi, emblaModalThumbsApi]);

    const onModalSelect = useCallback(() => {
        if (!emblaModalApi) return;
        const newIndex = emblaModalApi.selectedScrollSnap();
        setModalSelectedIndex(newIndex);
        setModalPrevBtnEnabled(emblaModalApi.canScrollPrev());
        setModalNextBtnEnabled(emblaModalApi.canScrollNext());
        if (emblaModalThumbsApi) emblaModalThumbsApi.scrollTo(newIndex);
    }, [emblaModalApi, emblaModalThumbsApi]);

    useEffect(() => {
        if (!emblaModalApi) return;
        setModalScrollSnaps(emblaModalApi.scrollSnapList());
        onModalSelect();
        emblaModalApi.on('select', onModalSelect);
        emblaModalApi.on('reInit', onModalSelect);
    }, [emblaModalApi, onModalSelect]);

    useEffect(() => {
        if (!isModalOpen) return;
        if (!emblaModalApi) return;
        emblaModalApi.scrollTo(modalStartIndex);
        setModalSelectedIndex(modalStartIndex);
    }, [isModalOpen, modalStartIndex, emblaModalApi]);

    const modalScrollPrev = useCallback(() => {
        if (!emblaModalApi) return;
        emblaModalApi.scrollPrev();
    }, [emblaModalApi]);

    const modalScrollNext = useCallback(() => {
        if (!emblaModalApi) return;
        emblaModalApi.scrollNext();
    }, [emblaModalApi]);

    useEffect(() => {
        if (!isModalOpen || typeof window === 'undefined') return;
        const handleKeyDown = (event: Event) => {
            if ((event as KeyboardEvent).key === 'Escape') {
                setIsModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!isModalOpen) {
            document.body.style.removeProperty('overflow');
            return;
        }
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isModalOpen]);

    const openModalAt = useCallback((index: number) => {
        if (!slideItems[index]) return;
        setModalStartIndex(index);
        setIsModalOpen(true);
    }, [slideItems]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const getArrowClasses = (direction: string) => {
        const base = `hidden md:flex embla__button embla__button--${direction} transition-opacity duration-200`;
        if (!hasMultipleSlides) {
            return base;
        }
        return `${base} opacity-0 group-hover:opacity-100 focus-visible:opacity-100`;
    };

    return (
        <div className="embla group relative">
            {/* Основная карусель */}
            <div className="embla__viewport" ref={emblaMainRef}>
                <div className="embla__container">
                    {slideItems.map((src, index) => (
                        <div className="embla__slide" key={index}>
                            <div
                                className="embla__slide__number cursor-zoom-in overflow-hidden md:!rounded-2xl"
                                role="button"
                                tabIndex={0}
                                onClick={() => openModalAt(index)}
                                onKeyDown={(event: React.KeyboardEvent) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openModalAt(index);
                                    }
                                }}
                                aria-label="Увеличить изображение"
                            >
                                <Image
                                    src={src}
                                    width={500}
                                    height={500}
                                    alt="Picture of the author"
                                    priority={index === 0}
                                    sizes="(max-width: 768px) 100vw, 48rem"
                                    unoptimized
                                    className="w-full h-full"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: imageFit,
                                        backgroundColor: 'transparent'
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Стрелки */}
                {hasMultipleSlides && (
                    <>
                        {prevBtnEnabled && (
                            <button
                                className={getArrowClasses('prev')}
                                onClick={scrollPrev}
                                aria-label="Предыдущее изображение"
                            >
                                <ChevronLeft />
                            </button>
                        )}
                        {nextBtnEnabled && (
                            <button
                                className={getArrowClasses('next')}
                                onClick={scrollNext}
                                aria-label="Следующее изображение"
                            >
                                <ChevronRight />
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Дотсы */}
            {hasMultipleSlides && (
                <div className="embla__dots absolute inset-x-0 bottom-3 justify-center md:hidden">
                    {scrollSnaps.map((_, dotIndex) => (
                        <button
                            key={dotIndex}
                            className={
                                dotIndex === selectedIndex
                                    ? 'embla__dot embla__dot--selected'
                                    : 'embla__dot'
                            }
                            onClick={() => onDotClick(dotIndex)}
                            aria-label={`Перейти к изображению ${dotIndex + 1}`}
                        />
                    ))}
                </div>
            )}

            {/* Карусель миниатюр */}
            {hasMultipleSlides && (
                <div className="embla-thumbs hidden md:block">
                    <div className="embla-thumbs__viewport" ref={emblaThumbsRef}>
                        <div className="embla-thumbs__container">
                            {slideItems.map((src, index) => (
                                <Thumb
                                    key={index}
                                    onClick={() => onThumbClick(index)}
                                    selected={index === selectedIndex}
                                    src={src}
                                    imageFit={imageFit}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {isModalOpen && isMounted && createPortal(
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/75 px-4 py-6">
                    <div
                        className="absolute inset-0"
                        onClick={closeModal}
                        aria-hidden="true"
                    />
                    <div className="relative z-10 flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white p-6 shadow-xl max-h-[calc(100vh-2rem)]">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="absolute right-4 top-4 text-gray-500 transition-colors hover:text-gray-700"
                            aria-label="Закрыть галерею"
                        >
                            <X size={20} />
                        </button>
                        <div className="embla group flex h-full flex-col overflow-hidden">
                            <div className="embla__viewport flex-1" ref={emblaModalRef}>
                                <div className="embla__container h-full">
                                    {slideItems.map((src, index) => (
                                        <div className="embla__slide h-full" key={`modal-${index}`}>
                                            <div className="embla__slide__number embla__slide__number--modal">
                                                <Image
                                                    src={src}
                                                    width={900}
                                                    height={900}
                                                    alt="Увеличенное изображение товара"
                                                    priority={index === 0}
                                                    unoptimized
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {hasMultipleSlides && (
                                    <>
                                        {modalPrevBtnEnabled && (
                                            <button
                                                className={getArrowClasses('prev')}
                                                onClick={modalScrollPrev}
                                                aria-label="Предыдущее изображение"
                                            >
                                                <ChevronLeft />
                                            </button>
                                        )}
                                        {modalNextBtnEnabled && (
                                            <button
                                                className={getArrowClasses('next')}
                                                onClick={modalScrollNext}
                                                aria-label="Следующее изображение"
                                            >
                                                <ChevronRight />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                            {hasMultipleSlides && (
                                <div className="embla-thumbs mt-6">
                                    <div className="embla-thumbs__viewport" ref={emblaModalThumbsRef}>
                                        <div className="embla-thumbs__container">
                                            {slideItems.map((src, index) => (
                                                <Thumb
                                                    key={`modal-thumb-${index}`}
                                                    onClick={() => onModalThumbClick(index)}
                                                    selected={index === modalSelectedIndex}
                                                    src={src}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {hasMultipleSlides && (
                                <div className="embla__dots mt-6">
                                    {modalScrollSnaps.map((_, dotIndex) => (
                                        <button
                                            key={`modal-dot-${dotIndex}`}
                                            className={
                                                dotIndex === modalSelectedIndex
                                                    ? 'embla__dot embla__dot--selected'
                                                    : 'embla__dot'
                                            }
                                            onClick={() => onModalThumbClick(dotIndex)}
                                            aria-label={`Перейти к изображению ${dotIndex + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ProductImageSlider;
